// Achievement definitions for CommandLearn

const achievements = [
  // Milestone achievements
  {
    id: 'first_blood',
    name: 'First Blood',
    description: 'Complete your first lesson',
    icon: '🎯',
    category: 'milestone',
    condition: (state) => getTotalCompletedLessons(state) >= 1
  },
  {
    id: 'getting_started',
    name: 'Getting Started',
    description: 'Complete 10 lessons',
    icon: '📚',
    category: 'milestone',
    condition: (state) => getTotalCompletedLessons(state) >= 10
  },
  {
    id: 'dedicated_learner',
    name: 'Dedicated Learner',
    description: 'Complete 50 lessons',
    icon: '🎓',
    category: 'milestone',
    condition: (state) => getTotalCompletedLessons(state) >= 50
  },
  {
    id: 'centurion',
    name: 'Centurion',
    description: 'Complete 100 lessons',
    icon: '💯',
    category: 'milestone',
    condition: (state) => getTotalCompletedLessons(state) >= 100
  },

  // Streak achievements
  {
    id: 'streak_5',
    name: 'On Fire',
    description: 'Get a 5 answer streak',
    icon: '🔥',
    category: 'streak',
    condition: (state) => state.bestStreak >= 5
  },
  {
    id: 'streak_10',
    name: 'Unstoppable',
    description: 'Get a 10 answer streak',
    icon: '⚡',
    category: 'streak',
    condition: (state) => state.bestStreak >= 10
  },
  {
    id: 'streak_25',
    name: 'Legendary',
    description: 'Get a 25 answer streak',
    icon: '🌟',
    category: 'streak',
    condition: (state) => state.bestStreak >= 25
  },
  {
    id: 'streak_50',
    name: 'Godlike',
    description: 'Get a 50 answer streak',
    icon: '👑',
    category: 'streak',
    condition: (state) => state.bestStreak >= 50
  },

  // Speed achievements
  {
    id: 'quick_draw',
    name: 'Quick Draw',
    description: 'Answer 5 lessons in under 5 seconds each',
    icon: '⏱️',
    category: 'speed',
    condition: (state) => state.fastAnswers >= 5
  },
  {
    id: 'speed_demon',
    name: 'Speed Demon',
    description: 'Answer 25 lessons in under 5 seconds each',
    icon: '🏎️',
    category: 'speed',
    condition: (state) => state.fastAnswers >= 25
  },
  {
    id: 'lightning_reflexes',
    name: 'Lightning Reflexes',
    description: 'Answer 100 lessons in under 5 seconds each',
    icon: '⚡',
    category: 'speed',
    condition: (state) => state.fastAnswers >= 100
  },

  // Skill achievements
  {
    id: 'no_hints_10',
    name: 'Self-Reliant',
    description: 'Complete 10 lessons without using hints',
    icon: '💪',
    category: 'skill',
    condition: (state) => state.lessonsWithoutHints >= 10
  },
  {
    id: 'no_hints_50',
    name: 'Pure Skill',
    description: 'Complete 50 lessons without using hints',
    icon: '🧠',
    category: 'skill',
    condition: (state) => state.lessonsWithoutHints >= 50
  },
  {
    id: 'no_hints_100',
    name: 'Memory Master',
    description: 'Complete 100 lessons without using hints',
    icon: '🏆',
    category: 'skill',
    condition: (state) => state.lessonsWithoutHints >= 100
  },

  // Topic mastery achievements
  {
    id: 'topic_master_bash',
    name: 'Bash Master',
    description: 'Complete all unique Bash lessons',
    icon: '🐧',
    category: 'mastery',
    condition: (state) => (state.completedLessons.bash?.length || 0) >= 26
  },
  {
    id: 'topic_master_linux',
    name: 'Linux Guru',
    description: 'Complete all unique Linux lessons',
    icon: '💻',
    category: 'mastery',
    condition: (state) => (state.completedLessons.linux?.length || 0) >= 20
  },
  {
    id: 'topic_master_git',
    name: 'Git Wizard',
    description: 'Complete all unique Git lessons',
    icon: '🔮',
    category: 'mastery',
    condition: (state) => (state.completedLessons.git?.length || 0) >= 20
  },
  {
    id: 'topic_master_terraform',
    name: 'Terraform Architect',
    description: 'Complete all unique Terraform lessons',
    icon: '🏗️',
    category: 'mastery',
    condition: (state) => (state.completedLessons.terraform?.length || 0) >= 20
  },
  {
    id: 'topic_master_ansible',
    name: 'Ansible Automator',
    description: 'Complete all unique Ansible lessons',
    icon: '🤖',
    category: 'mastery',
    condition: (state) => (state.completedLessons.ansible?.length || 0) >= 20
  },
  {
    id: 'topic_master_docker',
    name: 'Docker Captain',
    description: 'Complete all unique Docker lessons',
    icon: '🐳',
    category: 'mastery',
    condition: (state) => (state.completedLessons.docker?.length || 0) >= 30
  },
  {
    id: 'topic_master_networking',
    name: 'Network Ninja',
    description: 'Complete all unique Networking lessons',
    icon: '🌐',
    category: 'mastery',
    condition: (state) => (state.completedLessons.networking?.length || 0) >= 30
  },
  {
    id: 'topic_master_vim',
    name: 'Vim Virtuoso',
    description: 'Complete all unique Vim lessons',
    icon: '📝',
    category: 'mastery',
    condition: (state) => (state.completedLessons.vim?.length || 0) >= 30
  },

  // Boss battle achievements
  {
    id: 'boss_first',
    name: 'Boss Hunter',
    description: 'Complete your first boss battle',
    icon: '⚔️',
    category: 'boss',
    condition: (state) => state.bossesDefeated >= 1
  },
  {
    id: 'boss_slayer',
    name: 'Boss Slayer',
    description: 'Complete 5 boss battles',
    icon: '🗡️',
    category: 'boss',
    condition: (state) => state.bossesDefeated >= 5
  },
  {
    id: 'boss_destroyer',
    name: 'Boss Destroyer',
    description: 'Complete 10 boss battles',
    icon: '💀',
    category: 'boss',
    condition: (state) => state.bossesDefeated >= 10
  },

  // Daily challenge achievements
  {
    id: 'daily_first',
    name: 'Daily Challenger',
    description: 'Complete your first daily challenge',
    icon: '📅',
    category: 'daily',
    condition: (state) => state.dailyStreak >= 1
  },
  {
    id: 'daily_warrior',
    name: 'Daily Warrior',
    description: 'Complete daily challenges 7 days in a row',
    icon: '🛡️',
    category: 'daily',
    condition: (state) => state.dailyStreak >= 7
  },
  {
    id: 'daily_champion',
    name: 'Daily Champion',
    description: 'Complete daily challenges 30 days in a row',
    icon: '🏅',
    category: 'daily',
    condition: (state) => state.dailyStreak >= 30
  },

  // Level achievements
  {
    id: 'level_apprentice',
    name: 'Rising Star',
    description: 'Reach Level 2: Apprentice',
    icon: '⭐',
    category: 'level',
    condition: (state) => state.totalXP >= 100
  },
  {
    id: 'level_expert',
    name: 'Expert Status',
    description: 'Reach Level 5: Expert',
    icon: '🌟',
    category: 'level',
    condition: (state) => state.totalXP >= 1000
  },
  {
    id: 'level_master',
    name: 'Master Coder',
    description: 'Reach Level 6: Master',
    icon: '✨',
    category: 'level',
    condition: (state) => state.totalXP >= 1500
  },
  {
    id: 'level_legend',
    name: 'Living Legend',
    description: 'Reach Level 8: Legend',
    icon: '👑',
    category: 'level',
    condition: (state) => state.totalXP >= 3000
  },

  // XP achievements
  {
    id: 'xp_1000',
    name: 'Thousand Club',
    description: 'Earn 1,000 total XP',
    icon: '💰',
    category: 'xp',
    condition: (state) => state.totalXP >= 1000
  },
  {
    id: 'xp_5000',
    name: 'XP Hoarder',
    description: 'Earn 5,000 total XP',
    icon: '💎',
    category: 'xp',
    condition: (state) => state.totalXP >= 5000
  },
  {
    id: 'xp_10000',
    name: 'XP Millionaire',
    description: 'Earn 10,000 total XP',
    icon: '🤑',
    category: 'xp',
    condition: (state) => state.totalXP >= 10000
  }
];

// Helper function to count total completed lessons
function getTotalCompletedLessons(state) {
  return Object.values(state.completedLessons || {}).reduce(
    (total, lessons) => total + (lessons?.length || 0),
    0
  );
}

// Get achievement by ID
export const getAchievementById = (id) => {
  return achievements.find(a => a.id === id);
};

// Get all achievements
export const getAllAchievements = () => achievements;

// Get achievements by category
export const getAchievementsByCategory = (category) => {
  return achievements.filter(a => a.category === category);
};

// Get all achievement categories
export const getAchievementCategories = () => {
  return [...new Set(achievements.map(a => a.category))];
};

export default achievements;
